"use client";

import { createContext, useContext, ReactNode, useCallback, useMemo } from 'react';
import { useAuth } from '../components/AuthProvider';
import { fetchWithAuth } from '@/lib/utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';

export interface NutritionLog {
  id: number;
  log_date: string;
  total_calories: number;
  total_protein: number;
  total_carbs: number;
  total_fats: number;
  created_at: string;
  meal_items: MealItem[];
}

export interface MealItem {
  id: number;
  log_id: number;
  name: string;
  calories: number;
  protein: number;
  carbs: number;
  fats: number;
  created_at?: string;
}

interface BodyWeight {
  measurement_date: string;
  weight: number;
}

export interface ExerciseSet {
  reps: number;
  weight: number;
}

interface ExerciseHistoryEntry {
  id: number;
  exercise_id: number;
  user_id: string;
  workout_date: string;
  sets: number;
  reps: number;
  weight: number;
  created_at: string;
}

export interface Exercise {
  id: number;
  name: string;
  primary_muscles: string[];
  secondary_muscles: string[];
  sets: ExerciseSet[];
  exercise_history?: ExerciseHistoryEntry[];
}

export interface WeightGoal {
  id: number;
  user_id: string;
  target_weight: number;
  initial_weight: number;
  start_date: string;
  end_date?: string;
  status: 'active' | 'achieved' | 'cancelled' | 'expired';
  created_at: string;
  updated_at: string;
}

export interface UserProfile {
  full_name: string | null;
  email: string;
  created_at?: string;
}

export interface DataBundle {
  user: UserProfile | null;
  exercises: Exercise[];
  nutrition: NutritionLog[];
  bodyweight: BodyWeight[];
  weightGoals: WeightGoal[];
}

export interface DataContextType {
  data: DataBundle;
  profile: { maintenanceCalories?: number } | null;
  activeWeightGoal: WeightGoal | null;
  refreshData: () => Promise<void>;
  isLoading: boolean;
  authLoading: boolean;
}

const DataContext = createContext<DataContextType | undefined>(undefined);
const DataOverrideContext = createContext<DataContextType | null>(null);

/** Swap the signed-in user's bundle for another account's, e.g. a trainer viewing a client. */
export function DataOverrideProvider({
  value,
  children,
}: {
  value: DataContextType;
  children: ReactNode;
}) {
  return (
    <DataOverrideContext.Provider value={value}>
      {children}
    </DataOverrideContext.Provider>
  );
}

function usableName(value?: string | null) {
  const name = value?.trim()
  if (!name || name.includes('@')) return null
  return name
}

export function DataProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading, supabase } = useAuth();
  const queryClient = useQueryClient();

  const { data: fetchedData, isLoading: isDataLoading } = useQuery({
    queryKey: ['dataBundle', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const response = await fetchWithAuth('/api/data2/save');
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const jsonData = await response.json();
      return jsonData.success ? jsonData.data : null;
    },
    enabled: !!user,
  });

  const { data: account } = useQuery({
    queryKey: ['user-account', user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data } = await supabase
        .from('users')
        .select('full_name, email')
        .eq('id', user!.id)
        .single();
      return data;
    },
  });

  const { data: profile, isLoading: isProfileLoading } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const response = await fetchWithAuth('/api/user/profile');
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json();
    },
    enabled: !!user,
  });

  const data: DataBundle = useMemo(() => {
    const bundle = fetchedData || { exercises: [], nutrition: [], bodyweight: [], weightGoals: [], user: null };
    const metadata = user?.user_metadata as { full_name?: string; name?: string } | undefined;
    const fullName =
      usableName(account?.full_name) ??
      usableName(bundle.user?.full_name) ??
      usableName(metadata?.full_name) ??
      usableName(metadata?.name);

    return {
      ...bundle,
      user: {
        full_name: fullName,
        email: account?.email ?? bundle.user?.email ?? user?.email ?? '',
        created_at: bundle.user?.created_at,
      },
    };
  }, [account, fetchedData, user]);

  const activeWeightGoal = useMemo(() => {
    return data.weightGoals?.find((g: WeightGoal) => g.status === 'active') || 
           data.weightGoals?.find((g: WeightGoal) => g.status === 'expired') || 
           null;
  }, [data.weightGoals]);

  const refreshData = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['dataBundle', user?.id] });
    await queryClient.invalidateQueries({ queryKey: ['user-account', user?.id] });
    await queryClient.invalidateQueries({ queryKey: ['profile', user?.id] });
    // Also invalidate the weekly muscle sets graph to ensure it updates
    await queryClient.invalidateQueries({ queryKey: ['weeklyMuscleGroupSets'] });
  }, [queryClient, user?.id]);

  const isLoading = (isDataLoading || isProfileLoading) && !authLoading;

  return (
    <DataContext.Provider value={{ data, profile, activeWeightGoal, refreshData, isLoading, authLoading }}>
      {children}
    </DataContext.Provider>
  );
}

export function useData() {
  const override = useContext(DataOverrideContext);
  if (override) return override;

  const context = useContext(DataContext);
  if (undefined === context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
} 